/**
 * Firebase Cloud Messaging (FCM) initialisation for Sense nudge notifications.
 *
 * Flow overview:
 *  1. getPermission() is called from app.tsx on login when the nudge feature flag is on.
 *  2. We register the service worker (sense-notifications-sw.js) and wait for it to activate.
 *  3. We request notification permission from the browser.
 *  4. On grant  → fetch an FCM token and send it to the backend (via setToken callback).
 *     On deny  → invalidate the FCM token on Firebase and the backend (via deleteToken callback).
 *  5. We listen for runtime permission changes so tokens stay in sync.
 *  6. We listen for foreground push messages via onMessage and display them as OS notifications.
 *
 * The service worker handles background push display and notification-click deep-linking.
 */

import {initializeApp, getApps} from 'firebase/app';
import {
  deleteToken as firebaseDeleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';
import {AnalyticsService} from 'src/analytics';
import {NOTIFICATIONS_EVENTS} from './amplitude-events';
import logger from 'src/utils/logger';


const SENSE_NOTIFICATIONS_FIREBASE_APP_NAME = 'sense';

// SOC2: Firebase Web SDK API key for project `fluttersensemessaging`. Public-by-
// design per Google's docs -- ships to every browser, security enforced by
// Firebase Security Rules + GCP IAM, not by key secrecy. Kept hardcoded rather
// than externalized for the same reason as the SurveyJS license and the GCP
// keys in src/server/config.*.ts. Aikido finding 254009688 resolved with
// justification rather than externalization.
const senseNotificationsFirebaseConfig = {
  apiKey: 'AIzaSyAMf-ozAqh3DLozLC9GsnFKIo2NUEjbblM',
  authDomain: 'fluttersensemessaging.firebaseapp.com',
  projectId: 'fluttersensemessaging',
  storageBucket: 'fluttersensemessaging.firebasestorage.app',
  messagingSenderId: '85874655881',
  appId: '1:85874655881:web:2f1b117ac194ee2b0abc58',
  measurementId: 'G-7B8GF7BHQS',
};

// If the SW hasn't activated within this window, give up and proceed without it.
const SW_ACTIVATION_TIMEOUT_MS = 10_000;

// Lazily initialised to avoid running Firebase at module-load time (SSR safety).
const getFirebaseApp = () =>
  getApps().find((a) => a.name === SENSE_NOTIFICATIONS_FIREBASE_APP_NAME) ??
  initializeApp(
    senseNotificationsFirebaseConfig,
    SENSE_NOTIFICATIONS_FIREBASE_APP_NAME,
  );

export let messaging: Messaging | null = null;

let notificationSwRegistration: ServiceWorkerRegistration | null = null;

// ─── Service Worker Registration ────────────────────────────────────────────

/**
 * Wait for a service worker to reach the 'activated' state.
 * Rejects after SW_ACTIVATION_TIMEOUT_MS to avoid hanging indefinitely
 * (e.g. if the SW script has a syntax error and never activates).
 */
const waitForSwActivation = (serviceWorker: ServiceWorker): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      serviceWorker.removeEventListener('statechange', onStateChange);
      reject(new Error('Service worker activation timed out'));
    }, SW_ACTIVATION_TIMEOUT_MS);

    function onStateChange() {
      if (serviceWorker.state === 'activated') {
        clearTimeout(timeout);
        serviceWorker.removeEventListener('statechange', onStateChange);
        resolve();
      }
    }

    serviceWorker.addEventListener('statechange', onStateChange);
  });

/**
 * Register sense-notifications-sw.js and wait until it's active.
 *
 * Three possible states after register():
 *  - registration.active   → already installed from a previous visit, ready immediately.
 *  - registration.installing → first install, wait for activation.
 *  - registration.waiting   → updated SW waiting to activate (handled by skipWaiting in SW).
 *
 * The registration is stored in notificationSwRegistration so Firebase's getToken()
 * binds push delivery to our SW instead of its own default one.
 */
const registerNotificationServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  if (notificationSwRegistration) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      '/sense-notifications-sw.js',
    );

    if (registration.active) {
      notificationSwRegistration = registration;
      return;
    }

    const sw = registration.installing || registration.waiting;
    if (sw) {
      await waitForSwActivation(sw);
    }

    notificationSwRegistration = registration;
  } catch (error) {
    logger.error('Failed to register notifications service worker:', error);
  }
};

// ─── Token Helpers ──────────────────────────────────────────────────────────

/** Build getToken options — ties the FCM token to our Service Worker when available. */
const getTokenOptions = () =>
  notificationSwRegistration
    ? {serviceWorkerRegistration: notificationSwRegistration}
    : undefined;

/** Fetch a new FCM token and send it to the backend. */
const handlePermissionGranted = async (
  messagingInstance: Messaging,
  setToken: (token: string) => void,
) => {
  const token = await getToken(messagingInstance, getTokenOptions());
  await setToken(token);
};

/**
 * Clean up when notification permission is denied:
 *  1. Invalidate the token on Firebase so it stops delivering to this browser.
 *  2. Delete the token from our backend via the caller-provided callback.
 *
 * firebaseDeleteToken may throw if no token was ever created (e.g. first visit
 * with permission already denied) — safe to ignore.
 */
const handlePermissionDenied = async (
  messagingInstance: Messaging,
  deleteToken: () => void,
) => {
  try {
    await firebaseDeleteToken(messagingInstance);
    await deleteToken();
  } catch {
    // Token may not exist yet — safe to ignore
  }
};

// ─── Permission Change Listener ─────────────────────────────────────────────

/**
 * Watch for runtime permission changes (e.g. user toggles in browser settings).
 * Keeps the FCM token in sync without needing a page reload.
 */
const onPermissionChange = (
  messagingInstance: Messaging,
  setToken: (token: string) => void,
  deleteToken: () => void,
) => {
  if (!('permissions' in navigator)) {
    return;
  }

  navigator.permissions
    .query({name: 'notifications'})
    .then((notificationPerm) => {
      notificationPerm.onchange = async () => {
        if (notificationPerm.state === 'granted') {
          await handlePermissionGranted(messagingInstance, setToken);
        } else if (notificationPerm.state === 'denied') {
          await handlePermissionDenied(messagingInstance, deleteToken);
        }
      };
    });
};

// ─── Main Entry Point ───────────────────────────────────────────────────────

/**
 * Initialise FCM push notifications.
 *
 * Called once from app.tsx when the user is logged in and the nudge flag is enabled.
 *
 * @param setToken    — callback to persist the FCM token to the backend.
 * @param deleteToken — callback to remove the FCM token from the backend.
 */
export const getPermission = async ({
  setToken,
  deleteToken,
}: {
  setToken: (token: string) => void;
  deleteToken: () => void;
}) => {
  const supported = await isSupported();
  if (!supported) {
    return;
  }

  messaging = getMessaging(getFirebaseApp());
  if (!messaging) {
    return;
  }

  const messagingInstance = messaging;

  await registerNotificationServiceWorker();

  // Skip the permission prompt if already denied — go straight to cleanup.
  if (Notification.permission === 'denied') {
    await handlePermissionDenied(messagingInstance, deleteToken);
    onPermissionChange(messagingInstance, setToken, deleteToken);
    return;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await handlePermissionGranted(messagingInstance, setToken);
    } else {
      await handlePermissionDenied(messagingInstance, deleteToken);
    }
  } catch {
    // Permission request failed — still listen for future changes
  }

  onPermissionChange(messagingInstance, setToken, deleteToken);

  // ─── Foreground Message Handler ─────────────────────────────────────────
  // When the tab is active, FCM delivers via onMessage instead of the SW's push event.
  // We show an OS notification manually so the user still sees it.
  onMessage(messagingInstance, (payload) => {
    try {
      const notification = payload.notification;
      const data = payload.data;

      if (!notification) {
        return;
      }

      // Only display nudge-type events as OS notifications.
      if (!data || data.event_type !== 'nudge_event') {
        return;
      }

      const {title = '', body, icon} = notification;

      AnalyticsService.track(
        NOTIFICATIONS_EVENTS.PUSH_NOTIFICATION_REMINDER_RECEIVED,
        {
          notificationId: data.notification_id,
          notificationType: data.notification_type,
        },
      );

      if (notificationSwRegistration) {
        // Prefer SW-based notification — supports click handling via notificationclick.
        notificationSwRegistration.showNotification(title, {
          body,
          icon,
          tag: payload.messageId,
          data,
        });
      } else {
        // Fallback: plain Notification API (no SW click handler available).
        const notify = new Notification(title, {body, icon});
        notify.onclick = () => {
          notify.close();
          const deepLink = data.deep_link_url;
          if (deepLink) {
            const url = new URL(deepLink);
            if (data.notification_id) {
              url.searchParams.set('notificationId', data.notification_id);
            }
            if (data.notification_type) {
              url.searchParams.set('notificationType', data.notification_type);
            }
            window.open(url.href, '_blank');
          }
        };
      }
    } catch (error) {
      logger.error('Error handling Firebase message:', error);
    }
  });
};
